export interface UsesItem {
  name: string;
  description?: string;
  url?: string;
  affiliate?: boolean;
}

export interface UsesCategory {
  title: string;
  icon: string;
  items: UsesItem[];
}

export const usesData: UsesCategory[] = [
  {
    title: 'Computers',
    icon: 'computer',
    items: [
      {
        name: 'MacBook Pro 16" M1 Max — 64GB RAM',
        description:
          'Daily driver for all development work. The M1 Max handles everything from heavy Docker workloads to running multiple dev servers without breaking a sweat.',
        url: 'https://www.apple.com/shop/buy-mac/macbook-pro',
      },
      {
        name: 'Gaming PC — AMD Ryzen 9800X3D + NVIDIA RTX 5080',
        description:
          'Built for gaming and GPU-intensive workloads. The 9800X3D is a beast for single-threaded performance.',
      },
    ],
  },
  {
    title: 'Editor & IDE',
    icon: 'code',
    items: [
      {
        name: 'Cursor',
        description:
          'Primary editor. AI-native fork of VS Code that has fundamentally changed how I write code.',
        url: 'https://cursor.com',
      },
      {
        name: 'VS Code',
        description: 'Still use it occasionally for lighter editing and specific extension workflows.',
        url: 'https://code.visualstudio.com',
      },
    ],
  },
  {
    title: 'Terminal',
    icon: 'terminal',
    items: [
      {
        name: 'Ghostty',
        description:
          'New go-to terminal emulator. Native, fast, and the configuration is delightfully simple.',
        url: 'https://ghostty.org',
      },
      {
        name: 'iTerm2',
        description:
          'Still my primary terminal most of the time — my tmux setup and dotfiles are deeply tied to it.',
        url: 'https://iterm2.com',
      },
      {
        name: 'fish + bash + zsh',
        description:
          'Run all three depending on context. zsh with Powerlevel10k, Oh My Zsh, and Starship. Everything wrapped in tmux.',
      },
      {
        name: 'tmux',
        description: 'Terminal multiplexer. Session persistence and split panes are non-negotiable.',
        url: 'https://github.com/tmux/tmux',
      },
    ],
  },
  {
    title: 'Browser',
    icon: 'browser',
    items: [
      {
        name: 'Google Chrome',
        description: 'DevTools are unmatched. The ecosystem of extensions keeps me here.',
        url: 'https://www.google.com/chrome/',
      },
      {
        name: 'Ladybird',
        description:
          'Waiting on this one. A truly independent browser engine built from scratch — no fork, no user monetization, backed by a 501(c)(3) non-profit. Alpha targeting 2026.',
        url: 'https://ladybird.org',
      },
    ],
  },
  {
    title: 'Desk Setup',
    icon: 'desk',
    items: [
      {
        name: 'UPERFECT 17.3" Portable 4K QLED Monitor',
        description:
          '120Hz, 0.1ms, 500 nits, 3840×2160 UHD, 100% DCI-P3. Surprisingly great as an ultra-portable second screen — USB-C and HDMI with VESA mount.',
        url: 'https://www.uperfectmonitor.com',
      },
      {
        name: 'RK ROYAL KLUDGE RK61 60%',
        description:
          'Compact 60% mechanical keyboard with brown switches. Tri-mode (2.4GHz / Bluetooth / Wired), RGB, hot-swappable. Travels well.',
        url: 'https://rkgamingstore.com/products/rk61-60-compact-keyboard',
      },
      {
        name: 'Razer Basilisk V3 X HyperSpeed',
        description:
          'Wireless gaming mouse with 18K optical sensor. 535-hour battery life means I basically never charge it.',
        url: 'https://www.razer.com/gaming-mice/razer-basilisk-v3-x-hyperspeed',
      },
      {
        name: 'Shure SM7B',
        description:
          'The industry-standard dynamic microphone. Uses it for calls, recordings, and anything voice.',
        url: 'https://www.shure.com/en-US/products/microphones/sm/sm7b',
      },
      {
        name: 'Insta360 Link 2 Pro',
        description:
          'AI-powered webcam with gesture control and automatic tracking. Great for video calls.',
        url: 'https://www.insta360.com/product/insta360-link2-pro',
      },
      {
        name: 'SoundMAGIC E11C',
        description:
          'Wired HiFi earbuds with mic. Noise-isolating, powerful bass, tangle-free cord. Simple and reliable.',
        url: 'https://www.soundmagicheadphones.com/products/soundmagic-e11c',
      },
      {
        name: 'SteelSeries Arctis Nova Elite',
        description:
          'Gaming headset with premium Hi-Fi drivers, active noise cancellation, and 360° spatial audio. Hot-swappable battery system means zero downtime.',
        url: 'https://steelseries.com/gaming-headsets/arctis-nova-elite',
      },
      {
        name: 'Alesis Recital — 88-Key Digital Piano',
        description:
          'Sits right next to my desk. Doubles as a MIDI soundboard when I play games — nothing like triggering sound effects mid-match.',
        url: 'https://www.alesis.com/products/view2/recital',
      },
    ],
  },
  {
    title: 'Productivity',
    icon: 'productivity',
    items: [
      {
        name: 'Raycast',
        description:
          'Replaced Spotlight entirely. Clipboard history, snippets, window management, and extensions make it indispensable.',
        url: 'https://www.raycast.com',
      },
      {
        name: 'Bitwarden',
        description: 'Open-source password manager. Self-hostable, cross-platform, and free.',
        url: 'https://bitwarden.com',
      },
      {
        name: 'Figma',
        description: 'For design work, prototyping, and collaborating with designers.',
        url: 'https://www.figma.com',
      },
      {
        name: 'Spotify',
        description:
          "Can't code without music. Also the data source for the music page on this site.",
        url: 'https://open.spotify.com/user/cdbattaglia',
      },
      {
        name: 'Discord',
        description: 'Community and team communication. Where all the good dev communities live.',
        url: 'https://discord.com',
      },
    ],
  },
  {
    title: 'Development Tools',
    icon: 'devtools',
    items: [
      {
        name: 'Nix',
        description:
          'Reproducible development environments via flakes. Every project gets its own isolated, declarative toolchain.',
        url: 'https://nixos.org',
      },
      {
        name: 'direnv',
        description:
          'Automatic environment variable loading per directory. Paired with Nix flakes for seamless project switching.',
        url: 'https://direnv.net',
      },
      {
        name: 'Docker',
        description: 'Containerization for local services and production deployments.',
        url: 'https://www.docker.com',
      },
      {
        name: 'Homebrew',
        description: 'Still useful for system-level tools that live outside Nix.',
        url: 'https://brew.sh',
      },
      {
        name: 'GitHub',
        description: 'All code lives here. Actions for CI/CD.',
        url: 'https://github.com/cdbattags',
      },
    ],
  },
  {
    title: 'Infrastructure & Hosting',
    icon: 'cloud',
    items: [
      {
        name: 'DigitalOcean',
        description:
          'Primary hosting for personal projects. Simple, affordable, great developer experience.',
        url: 'https://www.digitalocean.com',
      },
      {
        name: 'Google Cloud (GCP)',
        description: 'Used professionally at Slabstack — Cloud Run, Cloud SQL, BigQuery, IAM.',
        url: 'https://cloud.google.com',
      },
      {
        name: 'Pulumi',
        description:
          'Infrastructure as code in real programming languages. Replaced Terraform for personal projects.',
        url: 'https://www.pulumi.com',
      },
      {
        name: 'Terraform',
        description:
          'Still use it professionally. HCL is fine once you stop fighting it.',
        url: 'https://www.terraform.io',
      },
      {
        name: 'Namecheap',
        description: 'Domain registrar. Straightforward pricing, no upsell games.',
        url: 'https://www.namecheap.com',
      },
      {
        name: 'PostHog',
        description:
          'Product analytics, session replay, feature flags. Open-source and self-hostable.',
        url: 'https://posthog.com',
      },
    ],
  },
  {
    title: 'AI',
    icon: 'ai',
    items: [
      {
        name: 'Gemini',
        description: 'Google\'s multimodal model. Deep integration with Search and workspace tools.',
        url: 'https://gemini.google.com',
      },
      {
        name: 't3.chat',
        description: 'Lightweight chat interface for multiple models. Fast, clean, no bloat.',
        url: 'https://t3.chat',
      },
      {
        name: 'ChatGPT',
        description: 'OpenAI\'s flagship. Still the go-to for general-purpose reasoning and writing.',
        url: 'https://chat.openai.com',
      },
      {
        name: 'Claude',
        description: 'Anthropic\'s model. Excels at long-context analysis and careful code review.',
        url: 'https://claude.ai',
      },
      {
        name: 'Grok',
        description: 'xAI\'s model with real-time information access.',
        url: 'https://grok.com',
      },
      {
        name: 'Kimi',
        description: 'Moonshot AI\'s long-context model. Handles massive documents with ease.',
        url: 'https://kimi.ai',
      },
      {
        name: 'OpenCode',
        description: 'Open-source terminal-native AI coding assistant. Built for developers who live in the CLI.',
        url: 'https://opencode.ai',
      },
      {
        name: 'Perplexity',
        description: 'For shopping.',
        url: 'https://www.perplexity.ai',
      },
      {
        name: 'PyTorch',
        description: 'The deep learning framework. Foundation for training and deploying models across research and production.',
        url: 'https://pytorch.org',
      },
    ],
  },
  {
    title: 'Music & Audio',
    icon: 'music',
    items: [
      {
        name: 'Alesis Recital — 88-Key Digital Piano',
        description:
          'Sits right next to my desk. Doubles as a MIDI soundboard when I play games — nothing like triggering sound effects mid-match.',
        url: 'https://www.alesis.com/products/view2/recital',
      },
      {
        name: 'Ardour',
        description:
          'Open-source DAW. Full-featured, cross-platform, and respects your freedom.',
        url: 'https://ardour.org',
      },
      {
        name: 'Ableton Live',
        description:
          'The other DAW. Session View is unmatched for live performance and experimentation.',
        url: 'https://www.ableton.com/en/live/',
      },
    ],
  },
  {
    title: 'Gaming',
    icon: 'gaming',
    items: [
      {
        name: 'League of Legends',
        url: 'https://www.leagueoflegends.com',
      },
      {
        name: 'Teamfight Tactics',
        url: 'https://teamfighttactics.leagueoflegends.com',
      },
      {
        name: 'Overwatch',
        url: 'https://overwatch.blizzard.com',
      },
      {
        name: 'Halo Infinite',
        url: 'https://www.halowaypoint.com/halo-infinite',
      },
      {
        name: 'Arc Raiders',
        url: 'https://www.arcraiders.com',
      },
      {
        name: 'World of Warcraft',
        url: 'https://worldofwarcraft.blizzard.com',
      },
      {
        name: 'Old School RuneScape',
        url: 'https://oldschool.runescape.com',
      },
      {
        name: 'Rust',
        url: 'https://rust.facepunch.com',
      },
      {
        name: 'Rocket League',
        url: 'https://www.rocketleague.com',
      },
    ],
  },
];
